import React, { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, RefreshCw, Phone, Mail, MapPin, Calendar, Shield, AlertTriangle, KeyRound, Users, FileText, LogOut } from "lucide-react";
import axios from "axios";
import { BUDGET_RANGES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://vachhraj-api.onrender.com'}/api`;

const AdminPage = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [authMode, setAuthMode] = useState("login");
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const [locked, setLocked] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0);
  const [contactSuperAdmin, setContactSuperAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState("enquiries");
  const [securityQuestion, setSecurityQuestion] = useState("");
  const [securityAnswer, setSecurityAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showUserModal, setShowUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role: "admin" });
  const [changingPassword, setChangingPassword] = useState(false);

  const token = typeof window !== 'undefined' ? localStorage.getItem("admin_token") : null;

  useEffect(() => {
    if (token) {
      checkAuth();
    }
    if (authMode === "recover") {
      fetchSecurityQuestion();
    }
  }, [token, authMode]);

  useEffect(() => {
    if (lockCountdown > 0) {
      const timer = setInterval(() => {
        setLockCountdown(prev => {
          if (prev <= 1) {
            setLocked(false);
            setLockCountdown(0);
            setError("");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockCountdown]);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data.user);
      fetchData();
    } catch (err) {
      localStorage.removeItem("admin_token");
      setUser(null);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/enquiries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnquiries(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (err) {
      setError("Failed to load users");
    }
  };

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API}/auth/activity-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs);
    } catch (err) {
      setError("Failed to load activity logs");
    }
  };

  useEffect(() => {
    if (user && activeTab === "users" && user.role === "super_admin") {
      fetchUsers();
    }
    if (user && activeTab === "logs" && user.role === "super_admin") {
      fetchLogs();
    }
  }, [user, activeTab]);

  const fetchSecurityQuestion = async () => {
    try {
      const res = await axios.get(`${API}/auth/security-question`);
      setSecurityQuestion(res.data.question);
    } catch (err) {
      setError("Failed to load security question");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (locked) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      if (res.data.success) {
        localStorage.setItem("admin_token", res.data.token);
        setUser(res.data.user);
        fetchData();
      } else if (res.data.locked) {
        setLocked(true);
        setLockCountdown(res.data.remaining_seconds || res.data.is_super_admin ? 300 : 86400);
        setContactSuperAdmin(res.data.contact_super_admin || false);
        setError(res.data.detail || "Account locked.");
      } else if (res.data.remaining_attempts !== undefined) {
        setRemainingAttempts(res.data.remaining_attempts);
        setContactSuperAdmin(res.data.contact_super_admin || false);
        setError(res.data.detail || `Invalid credentials. ${res.data.remaining_attempts} attempts remaining.`);
      } else {
        setError(res.data.detail || "Invalid credentials. Please check your email and password.");
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecover = async (e) => {
    e.preventDefault();
    if (locked) {
      setError("Too many attempts. Please wait 30 seconds.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/recover`, { 
        email,
        answer: securityAnswer,
        new_password: newPassword 
      });
      if (res.data.success) {
        setSuccessMessage("Password recovered! Please login with new password.");
        setAuthMode("login");
        setEmail("");
        setPassword("");
        setSecurityAnswer("");
        setNewPassword("");
      } else {
        setLocked(true);
        setError(res.data.detail || "Recovery failed. Locked for 30 seconds.");
        setTimeout(() => { setLocked(false); setError(""); }, 30000);
      }
    } catch (err) {
      setError("Recovery failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangingPassword(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (res.data.success) {
        setSuccessMessage("Password changed successfully!");
        setCurrentPassword("");
        setNewPassword("");
        setTimeout(() => { setSuccessMessage(""); setChangingPassword(false); }, 3000);
      } else {
        setError(res.data.detail || "Failed to change password");
        setChangingPassword(false);
      }
    } catch (err) {
      setError("Failed to change password.");
      setChangingPassword(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/users`, newUser, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setSuccessMessage("User created successfully!");
        setShowUserModal(false);
        setNewUser({ email: "", password: "", name: "", role: "admin" });
        fetchUsers();
      } else {
        setError(res.data.detail || "Failed to create user");
      }
    } catch (err) {
      setError("Failed to create user.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Are you sure you want to delete this user?")) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/auth/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsers();
    } catch (err) {
      setError("Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {}
    localStorage.removeItem("admin_token");
    setUser(null);
    setEnquiries([]);
    setUsers([]);
    setLogs([]);
    setEmail("");
    setPassword("");
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--ink-800)' }}>
        <div className="ve-card max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <Shield size={48} style={{ color: 'var(--gold-500)', margin: '0 auto' }} />
            <h1 className="ve-display mt-4 text-3xl">Secure Admin</h1>
            <p className="mt-2" style={{ color: 'var(--cream-300)' }}>Vachhraj Estate</p>
          </div>

          {authMode === "login" && (
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="ve-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@email.com"
                  className="ve-input"
                  disabled={locked || submitting}
                />
              </div>
              <div className="mb-4">
                <label className="ve-label">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className="ve-input pr-10"
                    disabled={locked || submitting}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-0 top-0 h-full pr-3"
                    style={{ color: 'var(--cream-500)' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              {locked && (
                <div className="mb-4 p-4 text-center" style={{ background: 'var(--terra-600)', borderRadius: '8px' }}>
                  <div className="text-2xl font-bold">
                    {lockCountdown >= 3600 
                      ? `${Math.floor(lockCountdown/3600)}h ${Math.floor((lockCountdown%3600)/60)}m`
                      : `${lockCountdown}s`
                    }
                  </div>
                  {contactSuperAdmin && (
                    <div className="mt-3">
                      <a 
                        href="https://wa.me/919512123570?text=Hi, I forgot my admin password. Can you help?" 
                        target="_blank" 
                        rel="noreferrer"
                        className="ve-btn-whatsapp text-xs py-2"
                      >
                        Contact Super Admin
                      </a>
                    </div>
                  )}
                </div>
              )}
              {error && !locked && (
                <p className="mb-4 text-sm" style={{ color: 'var(--terra-600)' }}>{error}</p>
              )}
              <button type="submit" className="ve-btn-primary w-full" disabled={locked || submitting}>
                {submitting ? (
                  <><RefreshCw size={14} className="animate-spin inline mr-1" /> Signing in...</>
                ) : locked ? (
                  <><Lock size={14} className="inline mr-1" /> Locked</>
                ) : (
                  <><Lock size={14} className="inline mr-1" /> Access Panel</>
                )}
              </button>
              {!locked && (
                <div className="mt-4 text-center text-xs" style={{ color: 'var(--cream-500)' }}>
                  {remainingAttempts <= 3 
                    ? `${remainingAttempts} attempts remaining` 
                    : contactSuperAdmin 
                      ? '5 attempts, then 24h lockout + contact super admin' 
                      : '5 attempts max'
                  }
                </div>
              )}
              <button
                type="button"
                onClick={() => { setAuthMode("recover"); setError(""); }}
                className="w-full mt-4 text-sm"
                style={{ color: 'var(--cream-500)' }}
              >
                <AlertTriangle size={14} className="inline mr-1" />
                Forgot Password?
              </button>
            </form>
          )}

          {authMode === "recover" && (
            <form onSubmit={handleRecover}>
              <div className="mb-4">
                <label className="ve-label">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your registered email"
                  className="ve-input"
                  disabled={locked || submitting}
                />
              </div>
              <div className="mb-4">
                <label className="ve-label">Security Question</label>
                <div className="p-3 rounded" style={{ background: 'var(--ink-700)', color: 'var(--cream-200)' }}>
                  {securityQuestion || "Loading..."}
                </div>
              </div>
              <div className="mb-4">
                <label className="ve-label">Your Answer</label>
                <input
                  type="text"
                  value={securityAnswer}
                  onChange={(e) => setSecurityAnswer(e.target.value)}
                  placeholder="Enter your answer"
                  className="ve-input"
                  disabled={locked || submitting}
                />
              </div>
              <div className="mb-4">
                <label className="ve-label">New Password</label>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="ve-input"
                  disabled={locked || submitting}
                />
              </div>
              {error && <p className="mb-4 text-sm" style={{ color: 'var(--terra-600)' }}>{error}</p>}
              {successMessage && <p className="mb-4 text-sm" style={{ color: 'var(--gold-500)' }}>{successMessage}</p>}
              <button type="submit" className="ve-btn-primary w-full" disabled={locked || submitting}>
                {submitting ? (
                  <><RefreshCw size={14} className="animate-spin inline mr-1" /> Recovering...</>
                ) : (
                  <><KeyRound size={14} className="inline mr-1" /> Recover Password</>
                )}
              </button>
              <button
                type="button"
                onClick={() => { setAuthMode("login"); setError(""); setSecurityAnswer(""); setNewPassword(""); }}
                className="w-full mt-4 text-sm"
                style={{ color: 'var(--cream-500)' }}
              >
                Back to Login
              </button>
            </form>
          )}

          <div className="mt-6 flex items-center justify-center gap-2 text-xs" style={{ color: 'var(--cream-500)' }}>
            <Shield size={12} />
            <span>256-bit Encrypted • Authorized Personnel Only</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--ink-900)', paddingTop: '2rem', paddingBottom: '4rem' }}>
      <div className="ve-container">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <Shield size={28} style={{ color: 'var(--gold-500)' }} />
              <h1 className="ve-display text-3xl">Admin Panel</h1>
            </div>
            <p className="mt-1" style={{ color: 'var(--cream-300)' }}>
              Logged in as: {user?.name} ({user?.role})
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={fetchData} disabled={loading} className="ve-btn-ghost">
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button onClick={handleLogout} className="ve-btn-ghost">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button
            onClick={() => setActiveTab("enquiries")}
            className={`px-4 py-2 text-sm ${activeTab === "enquiries" ? "ve-btn-primary" : "ve-btn-ghost"}`}
          >
            <FileText size={14} className="inline mr-1" />
            Enquiries ({enquiries.length})
          </button>
          <button
            onClick={() => setActiveTab("password")}
            className={`px-4 py-2 text-sm ${activeTab === "password" ? "ve-btn-primary" : "ve-btn-ghost"}`}
          >
            <KeyRound size={14} className="inline mr-1" />
            Change Password
          </button>
          {user?.role === "super_admin" && (
            <>
              <button
                onClick={() => setActiveTab("users")}
                className={`px-4 py-2 text-sm ${activeTab === "users" ? "ve-btn-primary" : "ve-btn-ghost"}`}
              >
                <Users size={14} className="inline mr-1" />
                Users ({users.length})
              </button>
              <button
                onClick={() => setActiveTab("logs")}
                className={`px-4 py-2 text-sm ${activeTab === "logs" ? "ve-btn-primary" : "ve-btn-ghost"}`}
              >
                <FileText size={14} className="inline mr-1" />
                Activity Logs
              </button>
            </>
          )}
        </div>

        {error && (
          <div className="ve-card mb-6" style={{ borderColor: 'var(--terra-600)' }}>
            <p style={{ color: 'var(--terra-600)' }}>{error}</p>
          </div>
        )}
        {successMessage && (
          <div className="ve-card mb-6" style={{ borderColor: 'var(--gold-500)' }}>
            <p style={{ color: 'var(--gold-500)' }}>{successMessage}</p>
          </div>
        )}

        {activeTab === "password" && (
          <div className="ve-card p-6">
            <h3 className="text-xl mb-4">Change Password</h3>
            <form onSubmit={handleChangePassword} className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <label className="ve-label text-xs">Current Password</label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    className="ve-input pr-10"
                    required
                  />
                  <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-0 top-0 h-full pr-3" style={{ color: 'var(--cream-500)' }}>
                    {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div className="flex-1">
                <label className="ve-label text-xs">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    className="ve-input pr-10"
                    required
                  />
                  <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-0 top-0 h-full pr-3" style={{ color: 'var(--cream-500)' }}>
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <button type="submit" className="ve-btn-primary" disabled={changingPassword}>
                {changingPassword ? <RefreshCw size={14} className="animate-spin inline mr-1" /> : null}
                {changingPassword ? "Updating..." : "Update"}
              </button>
            </form>
          </div>
        )}

        {activeTab === "users" && user?.role === "super_admin" && (
          <div className="ve-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl">Manage Users</h3>
              <button onClick={() => setShowUserModal(true)} className="ve-btn-primary">
                <Users size={14} className="inline mr-1" /> Add User
              </button>
            </div>
            <div className="space-y-3">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3" style={{ background: 'var(--ink-800)', borderRadius: '4px' }}>
                  <div>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-sm" style={{ color: 'var(--cream-300)' }}>{u.email}</div>
                    <div className="text-xs mt-1 px-2 py-0.5 inline-block" style={{ background: 'var(--ink-700)', color: 'var(--gold-500)' }}>{u.role}</div>
                  </div>
                  {u.id !== user?.id && (
                    <button onClick={() => handleDeleteUser(u.id)} className="ve-btn-ghost text-xs" style={{ color: 'var(--terra-600)' }}>
                      Delete
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === "logs" && user?.role === "super_admin" && (
          <div className="ve-card p-6">
            <h3 className="text-xl mb-4">Activity Logs</h3>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {logs.length === 0 ? (
                <p className="text-center py-8" style={{ color: 'var(--cream-300)' }}>No activity logs yet</p>
              ) : (
                logs.map((log, i) => (
                  <div key={i} className="flex items-center justify-between p-2 text-sm" style={{ borderBottom: '1px solid var(--hair)' }}>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs" style={{ 
                        background: log.action === 'LOGIN_SUCCESS' ? 'rgba(34, 197, 94, 0.2)' : 
                                   log.action === 'LOGIN_FAILED' ? 'rgba(220, 38, 38, 0.2)' : 
                                   log.action === 'ACCOUNT_LOCKED' ? 'rgba(234, 179, 8, 0.2)' : 'var(--ink-700)',
                        color: log.action === 'LOGIN_SUCCESS' ? '#22c55e' : 
                              log.action === 'LOGIN_FAILED' ? '#ef4444' : 
                              log.action === 'ACCOUNT_LOCKED' ? '#eab308' : 'var(--gold-500)'
                      }}>{log.action}</span>
                      <span style={{ color: 'var(--cream-200)' }}>{log.email}</span>
                      {log.details && <span className="text-xs" style={{ color: 'var(--cream-500)' }}>({log.details})</span>}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--cream-500)' }}>{formatDate(log.timestamp)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === "logs" && user?.role !== "super_admin" && (
          <div className="ve-card p-6 text-center">
            <p style={{ color: 'var(--cream-300)' }}>Only Super Admin can view activity logs.</p>
          </div>
        )}

        {activeTab === "enquiries" && (
          enquiries.length === 0 ? (
            <div className="ve-card text-center py-16">
              <p style={{ color: 'var(--cream-300)' }}>No enquiries yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {enquiries.map((enq, i) => (
                <div key={enq.id} className="ve-card">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="ve-number">#{enquiries.length - i}</span>
                        <h3 className="text-xl" style={{ fontFamily: 'Fraunces, serif' }}>{enq.name}</h3>
                        <span className="px-2 py-0.5 text-xs uppercase tracking-wider" style={{ background: 'var(--ink-700)', color: 'var(--gold-500)', border: '1px solid var(--hair)' }}>
                          {enq.interest}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" style={{ color: 'var(--cream-200)' }}>
                        <div className="flex items-center gap-2">
                          <Phone size={14} style={{ color: 'var(--gold-500)' }} />
                          <a href={`tel:${enq.phone}`} className="ve-link-underline">{enq.phone}</a>
                        </div>
                        {enq.email && (
                          <div className="flex items-center gap-2">
                            <Mail size={14} style={{ color: 'var(--gold-500)' }} />
                            <a href={`mailto:${enq.email}`} className="ve-link-underline">{enq.email}</a>
                          </div>
                        )}
                        {enq.area && (
                          <div className="flex items-center gap-2">
                            <MapPin size={14} style={{ color: 'var(--gold-500)' }} />
                            <span>{enq.area}</span>
                          </div>
                        )}
                        {enq.budget && (
                          <div>
                            <span style={{ color: 'var(--gold-500)' }}>Budget: </span>
                            <span>{BUDGET_RANGES.find(b => b.value === enq.budget)?.label || enq.budget}</span>
                          </div>
                        )}
                      </div>
                      {enq.message && (
                        <div className="mt-3 p-3" style={{ background: 'var(--ink-800)', borderRadius: '4px' }}>
                          <p className="text-sm" style={{ color: 'var(--cream-200)' }}>{enq.message}</p>
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 justify-end text-sm" style={{ color: 'var(--cream-500)' }}>
                        <Calendar size={14} />
                        {formatDate(enq.created_at)}
                      </div>
                      <div className="mt-2 flex gap-2 justify-end">
                        <a
                          href={`https://wa.me/91${enq.phone.replace(/[^0-9]/g, '')}?text=Hi ${encodeURIComponent(enq.name)}, thank you for your enquiry. How can I help?`}
                          target="_blank"
                          rel="noreferrer"
                          className="ve-btn-whatsapp text-xs py-2"
                        >
                          WhatsApp
                        </a>
                        <a href={`tel:${enq.phone}`} className="ve-btn-ghost text-xs py-2">
                          Call
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {showUserModal && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.8)', zIndex: 50 }}>
          <div className="ve-card max-w-md w-full mx-4 p-6">
            <h3 className="text-xl mb-4">Add New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="ve-label text-xs">Name</label>
                <input
                  type="text"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="User name"
                  className="ve-input"
                  required
                />
              </div>
              <div>
                <label className="ve-label text-xs">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@email.com"
                  className="ve-input"
                  required
                />
              </div>
              <div>
                <label className="ve-label text-xs">Password</label>
                <input
                  type="text"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="Set password"
                  className="ve-input"
                  required
                />
              </div>
              <div>
                <label className="ve-label text-xs">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="ve-input"
                >
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="submit" className="ve-btn-primary flex-1">Create</button>
                <button type="button" onClick={() => setShowUserModal(false)} className="ve-btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
